import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';

import {
  type CreateFleetVehicleInput,
  useCreateFleetVehicleMutation,
  useFleetDriversQuery,
  useFleetVehicleDetailQuery,
  useUpdateFleetVehicleMutation,
} from './useFleetQuery.js';

/**
 * The vehicle create / edit form. `mode: 'create'` renders a blank form that POSTs a new vehicle;
 * `mode: 'edit'` prefills from the `/fleet/vehicles/:id` detail query and PATCHes the update
 * (the AgreementForm mode pattern). Both carry the optional driver assignment (employee picker +
 * free-text label) + active flag. No tenant id is ever sent (SEC-11).
 */

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const TEXTAREA_CLASS =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';

function optionalOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export interface VehicleFormRouteProps {
  mode: 'create' | 'edit';
}

export function VehicleFormRoute({ mode }: VehicleFormRouteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEdit = mode === 'edit';

  // Route-agnostic param read: the `/fleet/$id/edit` route supplies `id`; the create route has none.
  const params = useParams({ strict: false }) as { id?: string };
  const rawId = params.id ?? '';
  const vehicleId = Number(rawId);

  const detailQuery = useFleetVehicleDetailQuery(isEdit ? vehicleId : 0);
  const driversQuery = useFleetDriversQuery();
  const createMutation = useCreateFleetVehicleMutation();
  const updateMutation = useUpdateFleetVehicleMutation(vehicleId);
  const mutation = isEdit ? updateMutation : createMutation;

  const [displayName, setDisplayName] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [vin, setVin] = useState('');
  const [assignedEmployeeId, setAssignedEmployeeId] = useState('');
  const [assignedDriverName, setAssignedDriverName] = useState('');
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Prefill from the detail query once it resolves (edit mode only).
  useEffect(() => {
    if (!isEdit || !detailQuery.isSuccess) return;
    const vehicle = detailQuery.data.vehicle;
    if (!vehicle) return;
    setDisplayName(vehicle.displayName);
    setUnitNumber(vehicle.unitNumber ?? '');
    setYear(vehicle.year ? String(vehicle.year) : '');
    setMake(vehicle.make ?? '');
    setModel(vehicle.model ?? '');
    setLicensePlate(vehicle.licensePlate ?? '');
    setVin(vehicle.vin ?? '');
    setAssignedEmployeeId(vehicle.assignedEmployeeId ? String(vehicle.assignedEmployeeId) : '');
    setAssignedDriverName(vehicle.assignedDriverName ?? '');
    setActive(vehicle.active);
    setNotes(vehicle.notes ?? '');
  }, [isEdit, detailQuery.isSuccess, detailQuery.data]);

  if (isEdit && detailQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsedYear = year.trim() ? Number(year) : null;
    setSuccessMessage(null);

    const input: CreateFleetVehicleInput = {
      display_name: displayName.trim(),
      unit_number: optionalOrNull(unitNumber),
      year: parsedYear !== null && Number.isFinite(parsedYear) ? parsedYear : null,
      make: optionalOrNull(make),
      model: optionalOrNull(model),
      license_plate: optionalOrNull(licensePlate),
      vin: optionalOrNull(vin),
      assigned_employee_id: assignedEmployeeId ? Number(assignedEmployeeId) : null,
      assigned_driver_name: optionalOrNull(assignedDriverName),
      active,
      notes: optionalOrNull(notes),
    };

    mutation.mutate(input, {
      onSuccess: () => {
        setSuccessMessage(
          isEdit ? t('fleet:vehicles.form.editSuccess') : t('fleet:vehicles.form.createSuccess'),
        );
        setTimeout(() => {
          void navigate({ to: isEdit ? '/fleet/$id' : '/fleet', params: { id: rawId } });
        }, 200);
      },
    });
  }

  const drivers = driversQuery.data?.drivers ?? [];

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {isEdit ? t('fleet:vehicles.form.editHeading') : t('fleet:vehicles.form.createHeading')}
        </h1>
      </header>

      <form
        onSubmit={submit}
        className="max-w-3xl space-y-4 rounded-md border border-border bg-card p-6"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="vehicle-display-name" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.displayName')}
            </label>
            <input
              id="vehicle-display-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={120}
              required
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="vehicle-unit-number" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.unitNumber')}
            </label>
            <input
              id="vehicle-unit-number"
              value={unitNumber}
              onChange={(event) => setUnitNumber(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="vehicle-year" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.year')}
            </label>
            <input
              id="vehicle-year"
              type="number"
              step="1"
              value={year}
              onChange={(event) => setYear(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="vehicle-make" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.make')}
            </label>
            <input
              id="vehicle-make"
              value={make}
              onChange={(event) => setMake(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="vehicle-model" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.model')}
            </label>
            <input
              id="vehicle-model"
              value={model}
              onChange={(event) => setModel(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="vehicle-license-plate" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.licensePlate')}
            </label>
            <input
              id="vehicle-license-plate"
              value={licensePlate}
              onChange={(event) => setLicensePlate(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="vehicle-vin" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.vin')}
            </label>
            <input
              id="vehicle-vin"
              value={vin}
              onChange={(event) => setVin(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="vehicle-driver" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.assignedDriver')}
            </label>
            <select
              id="vehicle-driver"
              value={assignedEmployeeId}
              onChange={(event) => setAssignedEmployeeId(event.target.value)}
              className={INPUT_CLASS}
            >
              <option value="">{t('fleet:vehicles.form.noDriver')}</option>
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="vehicle-driver-label" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.driverLabel')}
            </label>
            <input
              id="vehicle-driver-label"
              value={assignedDriverName}
              onChange={(event) => setAssignedDriverName(event.target.value)}
              maxLength={120}
              className={INPUT_CLASS}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 font-medium text-foreground text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                className="size-4 rounded border-border"
              />
              {t('fleet:vehicles.form.active')}
            </label>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="vehicle-notes" className={LABEL_CLASS}>
              {t('fleet:vehicles.form.notes')}
            </label>
            <textarea
              id="vehicle-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              className={TEXTAREA_CLASS}
            />
          </div>
        </div>

        {mutation.isError ? (
          <p role="alert" className="text-destructive text-sm">
            {isEdit ? t('fleet:vehicles.form.editError') : t('fleet:vehicles.form.createError')}
          </p>
        ) : null}
        {successMessage ? (
          <p role="status" className="text-secondary-foreground text-sm">
            {successMessage}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={mutation.isPending}>
            {isEdit ? t('fleet:vehicles.form.submitEdit') : t('fleet:vehicles.form.submitCreate')}
          </Button>
          <Button asChild variant="secondary">
            <Link to="/fleet">{t('fleet:vehicles.form.cancel')}</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
